import { useEffect, useState } from "react";
import { X, Printer, Sun, TrendingUp, Wallet, Users2, Coins, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmt2 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt4 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

/**
 * "Günü Bitir" Z-Rapor Modal — kısaca, şık ve baskıya hazır.
 * Data-testids: zreport-modal, z-modal-close, z-modal-print, z-modal-*
 */
export default function ZReportModal({ open, onClose }) {
  const [z, setZ] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/reports/z")
      .then((r) => setZ(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Z-Rapor çekilemedi"))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const profit = Number(z?.profit_estimate_tl || 0);
  const profitPositive = profit >= 0;
  const stats = [
    { icon: TrendingUp, label: "Toplam Satış",   value: `${fmt2(z?.sale_total || 0)} ₺`,  color: "var(--gold)", testid: "z-modal-sale" },
    { icon: Wallet,     label: "Nakit",           value: `${fmt2(z?.cash_total || 0)} ₺`,  color: "#22c55e",     testid: "z-modal-cash" },
    { icon: Users2,     label: "Kredi Kartı",     value: `${fmt2(z?.card_total || 0)} ₺`,  color: "#38bdf8",     testid: "z-modal-card" },
    { icon: Coins,      label: "Has Altın (Net)", value: `${fmt4(z?.net_gold_gram || 0)} gr`, color: "var(--gold)", testid: "z-modal-gold" },
  ];

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      data-testid="zreport-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl card-flat border relative"
        style={{ borderColor: "rgba(212,175,55,0.5)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Sun size={18} style={{ color: "var(--gold)" }} />
            <div>
              <div className="font-display text-lg tracking-widest">GÜNÜ KAPAT</div>
              <div className="label-caps text-[10px]" style={{ color: "var(--gold)" }}>
                Z Raporu · {z?.date || "…"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => window.print()}
              data-testid="z-modal-print"
              title="Yazdır"
              className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-[--gold] hover:text-[--gold] transition"
            >
              <Printer size={14} />
            </button>
            <button
              onClick={onClose}
              data-testid="z-modal-close"
              aria-label="Kapat"
              className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-red-400 hover:text-red-300 transition"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {loading || !z ? (
          <div className="p-12 flex flex-col items-center justify-center gap-3 text-neutral-500">
            <Loader2 size={22} className="animate-spin" />
            <div className="text-xs">Günün özeti hazırlanıyor…</div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-b border-white/5">
              {stats.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className={`p-5 ${i < stats.length - 1 ? "lg:border-r border-white/5" : ""} ${i % 2 === 0 ? "border-r lg:border-r border-white/5" : ""} ${i < 2 ? "border-b lg:border-b-0 border-white/5" : ""}`} data-testid={s.testid}>
                    <div className="flex items-center gap-1.5 label-caps text-[10px]">
                      <Icon size={12} style={{ color: s.color }} /> {s.label}
                    </div>
                    <div className="font-mono text-lg lg:text-xl mt-2 tabular-nums leading-tight" style={{ color: s.color }}>
                      {s.value}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-5 py-4 space-y-2">
              <div
                className="flex justify-between items-center py-3 border rounded-none px-3"
                style={{
                  borderColor: profitPositive ? "rgba(212,175,55,0.5)" : "rgba(239,68,68,0.5)",
                  background: profitPositive ? "rgba(212,175,55,0.06)" : "rgba(239,68,68,0.06)",
                }}
                data-testid="z-modal-profit"
              >
                <span className="label-caps flex items-center gap-1.5">
                  <Sparkles size={12} style={{ color: profitPositive ? "var(--gold)" : "#ef4444" }} />
                  Net Kâr / İşçilik (Tahmini)
                </span>
                <span
                  className="font-mono text-lg tabular-nums font-semibold"
                  style={{ color: profitPositive ? "var(--gold)" : "#ef4444" }}
                >
                  {profitPositive ? "" : "−"}{fmt2(Math.abs(profit))} ₺
                </span>
              </div>
              <div className="text-[10px] text-neutral-500 -mt-1 mb-1">
                Anlık Harem alış {fmt2(z?.gold_buy_price_used || 0)} ₺/gr üzerinden hesaplandı.
              </div>
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="label-caps">Hurda Alım Toplamı</span>
                <span className="font-mono tabular-nums">{fmt2(z.scrap_total)} ₺</span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="label-caps">İşlem Sayısı</span>
                <span className="font-mono tabular-nums" data-testid="z-modal-count">{z.transaction_count} adet</span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="label-caps">Havale/EFT (kalan)</span>
                <span className="font-mono tabular-nums text-neutral-400">
                  {fmt2(Math.max(0, (z.sale_total || 0) - (z.cash_total || 0) - (z.card_total || 0)))} ₺
                </span>
              </div>
            </div>

            {z.staff_performance && z.staff_performance.length > 0 && (
              <div className="px-5 pb-4">
                <div className="label-caps mb-2">Personel Performansı</div>
                <div className="space-y-1.5">
                  {z.staff_performance.map((s) => (
                    <div key={s.user_id} className="flex justify-between text-xs py-1.5 border-b border-white/5">
                      <span className="truncate">{s.name}</span>
                      <span className="font-mono tabular-nums text-neutral-400 shrink-0">
                        {s.count} işlem · {fmt2(s.total)} ₺
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="px-5 py-4 border-t border-white/5 text-[10px] text-neutral-500 font-mono tracking-widest text-center">
              MİLYEMSOFT · Z RAPORU · {new Date().toLocaleString("tr-TR")}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
